import { useCallback, useRef, useSyncExternalStore } from "react";
import { createAskChatBackend } from "../backends/ask";
import { createFirmChatBackend } from "../backends/firm";
import type { ChatBackend } from "../backends/types";
import { ChatController } from "../chat/controller";
import { type DocSettings, officeDocSettings } from "../chat/docKey";
import { PRODUCTS, type Product } from "../config";
import type { DocumentHands } from "../hands/types";
import { REQUIRED_WORD_API } from "../office";
import type { PairingSession } from "../session";
import { ChatPanel } from "./ChatPanel";
import { ConfirmScreen, PairingScreen, ProductChooser } from "./screens";

export interface AppProps {
  session: PairingSession;
  /** Null outside Word or when Word is older than the functional minimum. */
  hands: DocumentHands | null;
  /** Why `hands` is null, shown as a banner. */
  handsProblem: string | null;
  /** Injected in tests; defaults to the real per-product clients. */
  chatBackends?: Partial<Record<Product, () => ChatBackend>>;
  /** Injected in tests; defaults to `Office.context.document.settings`. */
  docSettings?: DocSettings | null;
  /** Show capture/upload numbers (`?debug=1`). */
  debug?: boolean;
}

const DEFAULT_CHAT: Record<Product, () => ChatBackend> = { ask: () => createAskChatBackend(), firm: () => createFirmChatBackend() };

export function debugFromLocation(): boolean {
  try {
    return new URLSearchParams(window.location.search).get("debug") === "1";
  } catch {
    return false;
  }
}

export function App({ session, hands, handsProblem, chatBackends, docSettings, debug = false }: AppProps) {
  const state = useSyncExternalStore(session.subscribe, session.getState);
  const touch = useCallback(() => void session.touch(), [session]);
  const signOut = useCallback(() => void session.signOut(), [session]);
  const reconnect = useCallback(() => void session.reconnect(), [session]);

  // One chat per pane session and account. It survives a reconnect to the SAME account (so the thread and
  // the ids of Ella's comments are kept); a different account starts from nothing.
  const chat = useRef<{ owner: string; controller: ChatController } | null>(null);
  if (state.phase === "connected") {
    const owner = `${state.product}\u0000${state.email}`;
    if (chat.current === null || chat.current.owner !== owner) {
      chat.current?.controller.reset();
      const product = state.product;
      chat.current = {
        owner,
        controller: new ChatController({
          hands,
          backend: (chatBackends?.[product] ?? DEFAULT_CHAT[product])(),
          token: () => session.authorizedToken(),
          onAuthLost: () => session.sessionLost(),
          onUserAction: touch,
          docSettings: docSettings === undefined ? officeDocSettings() : docSettings,
        }),
      };
    }
  }

  const connected = state.phase === "connected";
  return (
    <main className={`pane${connected ? " pane--chat" : ""}`}>
      <header className="pane__head">
        <h1 className="pane__brand">Ella</h1>
        {connected ? (
          <div className="pane__account">
            <span className="pane__who" title={`${state.email} · ${PRODUCTS[state.product].label}`}>
              {state.email}
            </span>
            <button type="button" className="link-btn" onClick={signOut}>
              Sign out
            </button>
          </div>
        ) : null}
      </header>
      {handsProblem !== null ? <div className="banner">{handsProblem}</div> : null}

      {state.phase === "choose" ? <ProductChooser onChoose={(p) => void session.choose(p)} /> : null}
      {state.phase === "starting" || state.phase === "verifying" ? <p className="small">Connecting…</p> : null}
      {state.phase === "pairing" ? (
        <PairingScreen
          product={state.product}
          userCode={state.userCode}
          connectUrl={state.connectUrl}
          expiresAt={state.expiresAt}
          onCancel={() => session.cancel()}
        />
      ) : null}
      {state.phase === "confirm" ? (
        <ConfirmScreen product={state.product} email={state.email} onConfirm={() => session.confirm()} onReject={signOut} />
      ) : null}
      {connected && chat.current !== null ? (
        <ChatPanel controller={chat.current.controller} handsReady={hands !== null} debug={debug} onReconnect={reconnect} />
      ) : null}
      {state.phase === "error" ? (
        <div className="stack">
          <div className="error" role="alert">
            {state.message}
          </div>
          <div className="row">
            {state.reconnect ? (
              <button type="button" className="btn btn--primary" onClick={reconnect}>
                Reconnect
              </button>
            ) : null}
            <button type="button" className={state.reconnect ? "btn btn--quiet" : "btn"} onClick={() => session.cancel()}>
              Start again
            </button>
          </div>
          {state.reconnect ? <p className="small">Your conversation in this pane is kept while you reconnect.</p> : null}
        </div>
      ) : null}
      {!connected ? <footer className="small">Jurisio · Word API {REQUIRED_WORD_API} or later</footer> : null}
    </main>
  );
}
